import { matchPath } from "react-router";
import {
  CreateTppWebFormV2Params,
  CreateTppWebFormV2ParamsMultibankingApiProviderEnum,
  CreateTppWebFormV2ParamsOperationEnum,
} from "@rentablo/api/apiv1";
import { isAppPath } from "./buildReturnUrl";

const { FIN_API, WEALTH_API } =
  CreateTppWebFormV2ParamsMultibankingApiProviderEnum;

export type WebFormEntry = {
  request: CreateTppWebFormV2Params;
  returnTo: string;
};

const toId = (value: string | null | undefined) => {
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : undefined;
};

const toOperation = (value: string | null) =>
  Object.values(CreateTppWebFormV2ParamsOperationEnum).find(
    (operation) => operation === value
  );

const getReturnTo = (search: URLSearchParams) => {
  const returnTo = search.get("returnTo");
  if (returnTo && isAppPath(returnTo)) return returnTo;

  const depotId = toId(search.get("depotId"));
  return depotId ? `/depots/${depotId}` : "/";
};

// A bank answering an in-app flow comes back to the same route with these.
const isBankAnswer = (search: URLSearchParams) =>
  search.has("code") || search.has("ticketId");

/**
 * The in-app routes that start an import or an update, as the web form request
 * that replaces them. Null for every other route, the bank's returns included.
 * The legal note is one of them only where the form shows it: elsewhere the
 * in-app note runs first and moves on to the import route.
 */
export const matchWebFormEntry = (
  pathname: string,
  search: URLSearchParams,
  { isLegalNoteInFlow = false }: { isLegalNoteInFlow?: boolean } = {}
): WebFormEntry | null => {
  const returnTo = getReturnTo(search);
  const bankConnectionId = toId(search.get("bankConnectionId"));
  const operation = toOperation(search.get("operation"));

  const finApiImport =
    matchPath("/import-bank-connection/:bankId", pathname) ??
    (isLegalNoteInFlow
      ? matchPath("/import-bank-connection/:bankId/legal-note", pathname)
      : null);
  const finApiBankId = toId(finApiImport?.params.bankId);
  if (finApiBankId) {
    return {
      returnTo,
      request: {
        multibankingApiProvider: FIN_API,
        bankSlug: String(finApiBankId),
        bankConnectionId,
        operation,
      },
    };
  }

  const wealthApiImport = matchPath(
    "/import-wealth-api-connection/:bankSlug",
    pathname
  );
  if (wealthApiImport?.params.bankSlug && !isBankAnswer(search)) {
    return {
      returnTo,
      request:
        bankConnectionId && operation
          ? { multibankingApiProvider: WEALTH_API, bankConnectionId, operation }
          : {
              multibankingApiProvider: WEALTH_API,
              bankSlug: wealthApiImport.params.bankSlug,
            },
    };
  }

  const finApiUpdate = matchPath(
    "/update-bank-connection/:bankConnectionId",
    pathname
  );
  const finApiConnectionId = toId(finApiUpdate?.params.bankConnectionId);
  if (finApiConnectionId) {
    return {
      returnTo,
      request: {
        multibankingApiProvider: FIN_API,
        bankConnectionId: finApiConnectionId,
      },
    };
  }

  const wealthApiUpdate = matchPath(
    "/update-wealth-api-connection/:connectionId",
    pathname
  );
  const wealthApiConnectionId = toId(wealthApiUpdate?.params.connectionId);
  if (wealthApiConnectionId && !isBankAnswer(search)) {
    return {
      returnTo,
      request: {
        multibankingApiProvider: WEALTH_API,
        bankConnectionId: wealthApiConnectionId,
      },
    };
  }

  return null;
};
